/**
 * Hero Content v2 (New Design)
 * 
 * Modern hero content with new UI components.
 * Mobile: Stacked content with circular stats
 * Desktop: Content card with rectangular stats (bordered card design)
 */

import Blur from 'components/atomic/atoms/blur';
import { Card } from 'components/atomic/atoms/card';
import FlexContainer from 'components/atomic/atoms/flex-container';
import { VerticalSpacing, VERTICAL_SPACING_VARIANTS } from 'components/atomic/atoms/vertical-spacing';
import ListingStatus, { LISTING_STATUS_SIZES } from 'components/listing-status';
import { Price, PRICE_VARIANTS } from 'ui/price';
import { PriceInfoTooltip } from 'ui/price-info-tooltip';
import { RecencyBadge } from 'ui/recency';
import { ADDRESS_VARIANTS } from 'ui/address';
import { PROPERTY_TYPE_VARIANTS } from 'ui/property-type';
import { AddressBlock } from 'ui/address-block';
import { Stats, STATS_VARIANTS } from 'ui/stats';
import { MlsInfo } from 'ui/mls-info';
import { VirtualTourButton } from 'ui/virtual-tour';
import { PriceHistoryButton } from 'ui/price-history-button';
import type { HeroContentProps } from '../types';
import mobileStyles from '../hero-mobile.module.scss';
import desktopStyles from '../hero-desktop.module.scss';

type ContentProps = Omit<HeroContentProps, 'variant'>;

export function HeroContentV2Mobile({
  listing,
  currency,
  locale,
  blur,
  mlsCourtesyText,
  mlsBuyerRepText,
  virtualTourLabel,
  onVirtualTourClick,
  priceHistoryLabel,
  onPriceHistoryClick,
  onAddressClick,
  listingSource,
}: ContentProps) {
  const priceProps = { amount: listing.price, currency, blur };
  const isFallback = listingSource === 'client-app-fallback';
  const addressHref = isFallback && listing.addressPath
    ? `${listing.addressPath}?listingSource=client-app-fallback`
    : listing.addressPath;

  return (
    <section className={mobileStyles.container} aria-label="Listing details">
      <div className={mobileStyles.content}>
        <div className={mobileStyles.priceRow}>
          <FlexContainer direction="row" align="center" gap={1} wrap sx={{ width: '100%' }}>
            <FlexContainer direction="row" align="center" gap={1} sx={{ flexShrink: 0 }}>
              <Price {...priceProps} locale={locale} variant={PRICE_VARIANTS.HERO} />
              <PriceInfoTooltip {...priceProps} />
            </FlexContainer>
            <FlexContainer direction="row" align="center" gap={1} wrap sx={{ maxWidth: '100%', flexShrink: 0 }}>
              <ListingStatus status={listing.standardStatus} isRental={listing.isRental ?? undefined} size={LISTING_STATUS_SIZES.SMALL} providerId={listing.providerId} mlsStatus={listing.mlsStatus} className={mobileStyles.statusPill} />
              <span className={mobileStyles.datePill}>
                <RecencyBadge timestamp={listing.hideMarketTime ? null : listing.addedAt} locale={locale} blur={blur} />
              </span>
            </FlexContainer>
          </FlexContainer>
        </div>

        <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.TIGHT} />

        <Blur active={blur}>
          <AddressBlock
            street={listing.streetAddress}
            city={listing.city}
            province={listing.province}
            unitNumber={listing.unitNumber}
            propertyType={listing.seoType}
            addressVariant={ADDRESS_VARIANTS.HERO}
            propertyTypeVariant={PROPERTY_TYPE_VARIANTS.HERO}
            href={addressHref}
            onClick={onAddressClick}
          />
        </Blur>

        <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.TIGHT} />

        <MlsInfo
          mlsNumber={listing.mlsNum ?? undefined}
          providerId={listing.providerId}
          courtesyText={mlsCourtesyText}
          buyerRepText={mlsBuyerRepText}
        />

        <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.NORMAL} />

        <Stats
          bedrooms={listing.bedrooms}
          bathrooms={listing.bathrooms}
          squareFootage={listing.squareFootage}
          parking={listing.parking}
          blur={blur}
          variant={STATS_VARIANTS.HERO_MOBILE}
        />

        {(priceHistoryLabel || listing.virtualTourUrl) && (
          <>
            <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.NORMAL} />
            <FlexContainer direction="row" gap={1} wrap>
              {priceHistoryLabel && (
                <PriceHistoryButton
                  label={priceHistoryLabel}
                  onClick={onPriceHistoryClick}
                />
              )}
              {listing.virtualTourUrl && virtualTourLabel && (
                <VirtualTourButton
                  label={virtualTourLabel}
                  virtualTourUrl={listing.virtualTourUrl}
                  onClick={onVirtualTourClick}
                />
              )}
            </FlexContainer>
          </>
        )}

        <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.NORMAL} />
      </div>
    </section>
  );
}

export function HeroContentV2Desktop({
  listing,
  currency,
  locale,
  blur,
  mlsCourtesyText,
  mlsBuyerRepText,
  virtualTourLabel,
  onVirtualTourClick,
  priceHistoryLabel,
  onPriceHistoryClick,
  onAddressClick,
  listingSource,
}: ContentProps) {
  const priceProps = { amount: listing.price, currency, blur };
  const isFallback = listingSource === 'client-app-fallback';
  const addressHref = isFallback && listing.addressPath
    ? `${listing.addressPath}?listingSource=client-app-fallback`
    : listing.addressPath;

  return (
    <section className={desktopStyles.container} aria-label="Listing details">
      <Card variant="border">
        <div className={desktopStyles.content}>
          <div className={desktopStyles.priceRow}>
            <FlexContainer direction="row" align="center" gap={1}>
              <Price {...priceProps} locale={locale} variant={PRICE_VARIANTS.HERO} />
              <PriceInfoTooltip {...priceProps} />
              <ListingStatus status={listing.standardStatus} isRental={listing.isRental ?? undefined} size={LISTING_STATUS_SIZES.SMALL} providerId={listing.providerId} mlsStatus={listing.mlsStatus} />
              <RecencyBadge timestamp={listing.hideMarketTime ? null : listing.addedAt} locale={locale} blur={blur} />
            </FlexContainer>
          </div>

          <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.TIGHT} />

          <Blur active={blur}>
            <AddressBlock
              street={listing.streetAddress}
              city={listing.city}
              province={listing.province}
              unitNumber={listing.unitNumber}
              propertyType={listing.seoType}
              addressVariant={ADDRESS_VARIANTS.HERO}
              propertyTypeVariant={PROPERTY_TYPE_VARIANTS.HERO}
              href={addressHref}
              onClick={onAddressClick}
            />
          </Blur>

          <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.EXTRA_TIGHT} />

          <MlsInfo
            mlsNumber={listing.mlsNum ?? undefined}
            providerId={listing.providerId}
            courtesyText={mlsCourtesyText}
            buyerRepText={mlsBuyerRepText}
          />

          <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.LARGE} />

          <Stats
            bedrooms={listing.bedrooms}
            bathrooms={listing.bathrooms}
            squareFootage={listing.squareFootage}
            parking={listing.parking}
            blur={blur}
            variant={STATS_VARIANTS.HERO_DESKTOP}
          />

          {(priceHistoryLabel || listing.virtualTourUrl) && (
            <>
              <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.NORMAL} />
              <FlexContainer direction="row" gap={1}>
                {priceHistoryLabel && (
                  <PriceHistoryButton
                    label={priceHistoryLabel}
                    onClick={onPriceHistoryClick}
                  />
                )}
                {listing.virtualTourUrl && virtualTourLabel && (
                  <VirtualTourButton
                    label={virtualTourLabel}
                    virtualTourUrl={listing.virtualTourUrl}
                    onClick={onVirtualTourClick}
                  />
                )}
              </FlexContainer>
            </>
          )}

          <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.LARGE} />
        </div>
      </Card>
    </section>
  );
}
